import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IcClose, IcRaiseHand, IcCalendar, IcPin, IcCheckCircle, IcSmile, IcBan } from 'components/common/icons/EmojiIcons';
import styles from './AttendancePrompt.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

type AttendanceStatus = 'ATTENDING' | 'NOT_ATTENDING' | 'UNDECIDED';

interface AttendancePromptProps {
    visible: boolean;
    meetingId?: string;
    meetingName: string;
    /** 모임 일자 라벨 (예: "2026년 5월 6일 (수)"). 일정 미정이면 undefined */
    meetingDate?: string;
    /** 모임 시간 라벨 (예: "오후 2:30"). 일정 미정이면 undefined */
    meetingTime?: string;
    /** 모임 장소 (예: "경기 의왕시 포일동 636"). 미정이면 undefined */
    meetingLocation?: string;
    initialStatus?: AttendanceStatus;
    initialLocationSharing?: boolean;
    onSubmit: (status: AttendanceStatus, locationSharing: boolean) => Promise<void> | void;
    onDismiss: () => void;
}

/**
 * 참석 여부 확인 팝업
 * - 모임 생성/참가 직후 노출
 * - 참석/미정/불참 3가지 선택
 * - 위치 공유 — 공유/미공유를 반드시 직접 고르게 합니다. 기본값은 두지 않습니다(null).
 *   예전 토글 방식은 기본값이 false 라 사용자가 몰랐다가 미공유로 굳어버렸습니다.
 *   반대로 공유를 기본값으로 두면 안내문을 안 읽고 저장한 사람의 위치가 공유됩니다.
 *   위치는 되돌리기 어려운 정보라, 어느 쪽으로도 기울이지 않고 본인이 고르게 합니다.
 */
export const AttendancePrompt: React.FC<AttendancePromptProps> = ({
    visible,
    meetingId,
    meetingName,
    meetingDate,
    meetingTime,
    meetingLocation,
    initialStatus = 'UNDECIDED',
    initialLocationSharing,
    onSubmit,
    onDismiss,
}) => {
    const { t } = useTranslation();
    const [status, setStatus] = useState<AttendanceStatus>(initialStatus);
    /* null = 아직 안 고름. 저장 버튼을 막아 본인이 고르게 합니다.
       전에 이 모임에서 고른 값이 있으면 그걸 살립니다. */
    const readSaved = (): boolean | null => {
        if (typeof initialLocationSharing === 'boolean') return initialLocationSharing;
        if (meetingId) {
            const saved = localStorage.getItem(`locationSharing_${meetingId}`);
            if (saved === 'true') return true;
            if (saved === 'false') return false;
        }
        return null;
    };
    const [locationSharing, setLocationSharing] = useState<boolean | null>(readSaved);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (visible) {
            setStatus(initialStatus);
            setLocationSharing(readSaved());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, initialStatus, initialLocationSharing, meetingId]);

    if (!visible) return null;

    // 위치공유는 ATTENDING 일 때만 의미가 있음. UNDECIDED/NOT_ATTENDING 이면 자동 false 로 저장.
    const isAttending = status === 'ATTENDING';
    const canSubmit = !submitting && (isAttending ? locationSharing !== null : true);

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const finalSharing = isAttending ? (locationSharing as boolean) : false;
            await onSubmit(status, finalSharing);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={cls("att-prompt-overlay")} onClick={onDismiss}>
            <div className={cls("att-prompt-card")} onClick={(e) => e.stopPropagation()}>
                <button className={cls("att-prompt-close")} onClick={onDismiss} aria-label="close"><IcClose size={16} /></button>
                <div className={cls("att-prompt-icon")}><IcRaiseHand size={32} /></div>
                <div className={cls("att-prompt-title")}>{t('meeting.attendancePrompt.title')}</div>
                <div className={cls("att-prompt-desc")}>
                    <strong>{meetingName}</strong> {t('meeting.attendancePrompt.desc')}
                </div>

                {/* 모임 정보 — 사용자가 일정/장소 보고 결정 가능하도록 */}
                {(meetingDate || meetingTime || meetingLocation) && (
                    <div style={{
                        margin: '8px 0 12px',
                        padding: '10px 12px',
                        background: '#f8fafc',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 12,
                        color: '#444',
                        textAlign: 'left',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                    }}>
                        {(meetingDate || meetingTime) && (
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <span style={{ color: '#888', minWidth: 32 }}><IcCalendar size={14} /></span>
                                <span>
                                    {meetingDate || ''}{meetingDate && meetingTime ? ' · ' : ''}{meetingTime || ''}
                                </span>
                            </div>
                        )}
                        {meetingLocation && (
                            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                                <span style={{ color: '#888', minWidth: 32 }}><IcPin size={14} /></span>
                                <span style={{ flex: 1, wordBreak: 'break-word' }}>{meetingLocation}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* 참석 여부 — 3가지 (참석/미정/불참) */}
                <div className={cls("att-prompt-status-row")}>
                    <button
                        className={cls(`att-prompt-status ${status === 'ATTENDING' ? 'selected attend' : ''}`)}
                        onClick={() => setStatus('ATTENDING')}
                    >
                        <IcCheckCircle size={16} /> {t('meeting.attendancePrompt.attend')}
                    </button>
                    <button
                        className={cls(`att-prompt-status ${status === 'UNDECIDED' ? 'selected undecided' : ''}`)}
                        onClick={() => setStatus('UNDECIDED')}
                    >
                        <IcSmile size={16} /> {t('meeting.attendancePrompt.undecided')}
                    </button>
                    <button
                        className={cls(`att-prompt-status ${status === 'NOT_ATTENDING' ? 'selected decline' : ''}`)}
                        onClick={() => setStatus('NOT_ATTENDING')}
                    >
                        <IcClose size={16} /> {t('meeting.attendancePrompt.decline')}
                    </button>
                </div>

                {/* 위치 공유 — ATTENDING 일 때만 노출 + 명시적 선택 강제 */}
                {isAttending && (
                    <>
                        <div className={cls("att-prompt-loc-label-block")}>
                            <div className={cls("att-prompt-loc-title")}><IcPin size={16} /> {t('meeting.attendancePrompt.locationShare')}</div>
                            <div className={cls("att-prompt-loc-sub")}>{t('meeting.attendancePrompt.locationShareSub')}</div>
                            <div className={cls("att-prompt-loc-notice")}>{t('meeting.attendancePrompt.locShareNotice')}</div>
                        </div>
                        <div className={cls("att-prompt-loc-choice-row")}>
                            <button
                                type="button"
                                className={cls(`att-prompt-loc-choice ${locationSharing === true ? 'selected' : ''}`)}
                                onClick={() => setLocationSharing(true)}
                            >
                                <IcCheckCircle size={16} /> {t('meeting.attendancePrompt.locShareOn')}
                            </button>
                            <button
                                type="button"
                                className={cls(`att-prompt-loc-choice ${locationSharing === false ? 'selected' : ''}`)}
                                onClick={() => setLocationSharing(false)}
                            >
                                <IcBan size={16} /> {t('meeting.attendancePrompt.locShareOff')}
                            </button>
                        </div>
                    </>
                )}

                <button className={cls("att-prompt-submit")} onClick={handleSubmit} disabled={!canSubmit}>
                    {submitting
                        ? t('common.saving')
                        : isAttending && locationSharing === null
                            ? t('meeting.attendancePrompt.pickLoc')
                            : t('meeting.attendancePrompt.submit')}
                </button>
                <button className={cls("att-prompt-later")} onClick={onDismiss}>
                    {t('meeting.attendancePrompt.later')}
                </button>
            </div>
        </div>
    );
};
